import { getRecurrencePeriodLabels } from '~/constants';
import { formatNumTimes } from '~/infra/utils';
import { BillingTriggerType } from '~/types/api.model';
import { BillingStageScheduleType, StagesTerms } from '../OneTimeInStagesModel';
import { RecurrencePeriodTypes } from './model';
import { isOnDemand, isRecurring } from './trigger-predicates';
export function recurrencePeriod(billing) {
    return isRecurring(billing) ? billing.recurrencePeriod : null;
}
export function isAutomatic(billing) {
    return billing.trigger === BillingTriggerType.OnApproval || billing.trigger === BillingTriggerType.Ongoing;
}
export function isManual(billing) {
    return billing.trigger === BillingTriggerType.OneTimeManual || billing.trigger === BillingTriggerType.RepeatableManual || billing.trigger === BillingTriggerType.OneTimeInStages || billing.trigger === BillingTriggerType.OnDemand;
}
export function isOnApproval(billing) {
    return billing.trigger === BillingTriggerType.OnApproval;
}
export function isOneTimeManual(billing) {
    return billing.trigger === BillingTriggerType.OneTimeManual;
}
export function isOngoing(billing) {
    return billing.trigger === BillingTriggerType.Ongoing;
}
export function isRepeatableManual(billing) {
    return billing.trigger === BillingTriggerType.RepeatableManual;
}
export function isOneTimeInStages(billing) {
    return billing.trigger === BillingTriggerType.OneTimeInStages;
}
export function oneTimeInStages(billing) {
    return isOneTimeInStages(billing) ? billing.oneTimeInStages : null;
}
export const triggerLabelsTextsPath = 'billing.triggerLabels';
function isManualStage(stage, terms) {
    if (stage.stageSchedule) {
        return stage.stageSchedule.type === BillingStageScheduleType.Manual;
    }
    if (terms === StagesTerms.AllManual) return true;
    return stage.stageNumber !== 1;
}
export function stagesTriggerLabel(oneTimeInStagesModel, texts) {
    const stages = oneTimeInStagesModel.stages();
    const count = stages.length;
    const terms = oneTimeInStagesModel.terms();
    const manualCount = stages.filter((stage)=>isManualStage(stage, terms)).length;
    if (manualCount === 0) return texts.automaticStages({
        count
    });
    if (manualCount === count) return texts.manualStages({
        count
    });
    return texts.partlyAutomaticStages({
        count
    });
}
export function preapprovalPeriod(billing) {
    return isOnDemand(billing) ? billing.preapprovalPeriod : null;
}
export function billingDayOfMonth(billing) {
    return isRecurring(billing) ? billing.billingDayOfMonth : null;
}
export function billingDayOfWeek(billing) {
    return isRecurring(billing) ? billing.billingDayOfWeek : null;
}
export function billingDayOfYear(billing) {
    return isRecurring(billing) ? billing.billingDayOfYear : null;
}
function hasMaxCharges(billing) {
    return (isRecurring(billing) || isOnDemand(billing)) && billing.maxCharges != null;
}
export function maxChargesEnabled(billing) {
    return hasMaxCharges(billing) ? billing.maxCharges.enabled : false;
}
export function maxCharges(billing) {
    return hasMaxCharges(billing) ? billing.maxCharges : null;
}
export function maxChargesLimit(billing) {
    return hasMaxCharges(billing) ? billing.maxCharges.limit : null;
}
export function maxChargesLeft(billing) {
    return hasMaxCharges(billing) ? billing.maxCharges.chargesLeft : null;
}
export function formatMaxChargesLimit(billing, commonTexts) {
    return formatNumTimes(maxChargesLimit(billing), commonTexts);
}
export function trigger(billing) {
    return billing.trigger;
}
export function isBilledUpfront(billing) {
    return isRecurring(billing) ? billing.isBilledUpfront : false;
}
/** @deprecated Use `isBillOnPeriodBeforeNextBilling` */ export function isSkipBillingOnAcceptance(billing) {
    return isOngoing(billing) ? billing.billUpfrontState.skipBillingOnAcceptance : false;
}
export function isYearly(billing) {
    return isRecurring(billing) && billing.recurrencePeriod === RecurrencePeriodTypes.Yearly;
}
export function isQuarterly(billing) {
    return isRecurring(billing) && billing.recurrencePeriod === RecurrencePeriodTypes.Quarterly;
}
export function isMonthly(billing) {
    return isRecurring(billing) && billing.recurrencePeriod === RecurrencePeriodTypes.Monthly;
}
export function isWeekly(billing) {
    return isRecurring(billing) && billing.recurrencePeriod === RecurrencePeriodTypes.Weekly;
}
export function isBiWeekly(billing) {
    return isRecurring(billing) && billing.recurrencePeriod === RecurrencePeriodTypes.BiWeekly;
}
export function isPaused(billing) {
    return isOngoing(billing) && billing.isPaused;
}
export function isProrated(billing) {
    return isOngoing(billing) && billing.isProrated;
}
export function periodRequiresBillingDayOfWeek(period) {
    const requiresDay = ()=>true;
    const noDay = ()=>false;
    return matchRecurrencePeriod(period, {
        weekly: requiresDay,
        biweekly: requiresDay,
        monthly: noDay,
        quarterly: noDay,
        yearly: noDay
    });
}
export function periodRequiresBillingDayOfYear(period) {
    return [
        RecurrencePeriodTypes.Yearly
    ].includes(period);
}
export function isBilledImmediatelyWhenApproved(billing) {
    return isOnApproval(billing) || isOngoing(billing) && billing.isBilledUpfront && !billing.isPaused && !billing.billUpfrontState.skipBillingOnAcceptance;
}
export function match(billing, visitor) {
    switch(billing.trigger){
        case BillingTriggerType.Ongoing:
            return visitor.ongoing(billing);
        case BillingTriggerType.RepeatableManual:
            return visitor.repeatableManual(billing);
        case BillingTriggerType.OneTimeManual:
            return visitor.oneTimeManual(billing);
        case BillingTriggerType.OneTimeInStages:
            return visitor.oneTimeInStages(billing);
        case BillingTriggerType.OnApproval:
            return visitor.onApproval(billing);
        case BillingTriggerType.OnDemand:
            return visitor.onDemand(billing);
    }
}
export function matchRecurrencePeriod(period, visitor) {
    switch(period){
        case RecurrencePeriodTypes.Weekly:
            return visitor.weekly();
        case RecurrencePeriodTypes.BiWeekly:
            return visitor.biweekly();
        case RecurrencePeriodTypes.Monthly:
            return visitor.monthly();
        case RecurrencePeriodTypes.Quarterly:
            return visitor.quarterly();
        case RecurrencePeriodTypes.Yearly:
            return visitor.yearly();
    }
}
export function extendedRecurrencePeriod(billing) {
    return match(billing, {
        onApproval: ()=>'onetime',
        ongoing: (ongoing)=>ongoing.recurrencePeriod,
        repeatableManual: (repeatableManual)=>repeatableManual.recurrencePeriod,
        oneTimeManual: ()=>'onetime',
        oneTimeInStages: ()=>'inStages',
        onDemand: ()=>'onDemand'
    });
}
export function extendedRecurrencePeriodText(extendedRecurrencePeriod, isInSentence, texts) {
    var _extraLabels_extendedRecurrencePeriod;
    const extraLabels = {
        onetime: isInSentence ? texts.onceInSentence() : texts.oneTimeSentence(),
        inStages: texts.inStages(),
        onDemand: texts.onDemand()
    };
    return (_extraLabels_extendedRecurrencePeriod = extraLabels[extendedRecurrencePeriod]) !== null && _extraLabels_extendedRecurrencePeriod !== void 0 ? _extraLabels_extendedRecurrencePeriod : getRecurrencePeriodLabels(texts)[extendedRecurrencePeriod];
}
